export class ConfiguratorError extends Error {
  public readonly status: number
  public readonly title: string

  constructor(status: number, title: string, message: string) {
    super(message)
    this.name = this.constructor.name
    this.status = status
    this.title = title
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

export class BadRequestError extends ConfiguratorError {
  constructor(title: string, message: string) { super(400, title, message) }
}

export class NotFoundError extends ConfiguratorError {
  constructor(title: string, message: string) { super(404, title, message) }
}

export type StatusCodeToError<T extends number> =
  T extends 400 ? BadRequestError
    : T extends 404 ? NotFoundError
      : ConfiguratorError

const statusToErrorClassMap: Record<number, new (title: string, message: string) => ConfiguratorError> = {
  400: BadRequestError,
  404: NotFoundError,
}

export const parseConfiguratorError = (status: number, json: unknown, expectedErrors: readonly number[]): ConfiguratorError => {
  if (!expectedErrors.includes(status)) {
    throw new ConfiguratorError(status, 'Unexpected error', `Unexpected HTTP status ${status}`)
  }

  const ErrorClass = statusToErrorClassMap[status]
  const body = json as Record<string, unknown> | null
  const title = (body && typeof body['title'] === 'string') ? body['title'] : 'Error'
  const message = (body && typeof body['detail'] === 'string') ? body['detail'] : `HTTP ${status}`

  if (ErrorClass) return new ErrorClass(title, message)
  return new ConfiguratorError(status, title, message)
}
